import { css } from '@emotion/react'
import { ONE_DAY } from '@mathflat/shared/@common/constants/date'
import type { CalenderDateStr } from '@mathflat/shared/@common/types/string'
import { CalendarDate } from '@mathflat/shared/@modules/(dto)/CalendarDate'
import { endOfMonth, format, startOfMonth } from 'date-fns'
import { type FC, useEffect, useState } from 'react'

import { errorHandlerService } from '~/@common/services'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import Calendar, { type CalendarDateRange } from '~/@common/ui/Calendar/Calendar'
import { Icon } from '~/@common/ui/Icon/Icon'
import Modal from '~/@common/ui/modal/Modal'

import { homeApi } from '../../@common/api'

// 학부모 학습홈 — 최대 30일까지만 기간 선택 가능 (서비스 정책)
const MAX_RANGE_DAYS = 30

type Range = { startDate: string; endDate: string }

type Props = {
  startDate: string
  endDate: string
  onChange: (range: Range) => void
}

/**
 * 학부모 학습홈 우상단 기간 표시 트리거 + 날짜 선택 모달.
 * 트리거 버튼 클릭 시 RangePicker 모달이 뜨고, "선택하기" 누르면 30일 검증 후 onChange.
 */
const ParentDateRangePicker: FC<Props> = ({ startDate, endDate, onChange }) => {
  const label = `${format(new Date(startDate), 'yyyy.MM.dd')}~${format(new Date(endDate), 'yyyy.MM.dd')}`

  const handleClick = () => {
    modalService.openModal(
      {
        header: '날짜 선택',
        content: (
          <ParentDateRangeModalContent
            initialRange={{ startDate, endDate }}
            onApply={(range) => {
              onChange(range)
              modalService.closeModal()
            }}
          />
        ),
      },
      {
        modalName: '학부모 학습홈 기간 선택',
        hasCloseButton: true,
        headerOption: { position: 'center', hasBottomBorder: false },
      },
    )
  }

  return (
    <button type="button" css={_TriggerStyle} onClick={handleClick}>
      <span>{label}</span>
      <Icon name="icon_calendar" size={20} color="var(--Colors-Gray-800)" />
    </button>
  )
}

export default ParentDateRangePicker

const _TriggerStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  /* margin-left: auto; */
  padding: 0;
  border: 0;
  background: transparent;
  ${typo.body02};
  font-weight: 400;
  color: var(--Colors-Gray-800);
  cursor: pointer;

  ${mediaQuery.underTablet} {
    margin-left: 0;
  }
`

type ModalContentProps = {
  initialRange: Range
  onApply: (range: Range) => void
}

const ParentDateRangeModalContent: FC<ModalContentProps> = ({ initialRange, onApply }) => {
  const [tempRange, setTempRange] = useState<CalendarDateRange>(() => ({
    start: new CalendarDate(initialRange.startDate),
    end: new CalendarDate(initialRange.endDate),
  }))

  const [viewingDate, setViewingDate] = useState<Date>(() => new Date(initialRange.endDate))
  const [specialDates, setSpecialDates] = useState<Set<CalenderDateStr>>(() => new Set())

  // viewing month 단위로 학습 기록 있는 날짜를 조회해 캘린더에 점 표시용 set 으로 보관
  useEffect(() => {
    const studentId = commonRepo.parent.activeChildStudentId
    if (!studentId) return

    const monthStart = format(startOfMonth(viewingDate), 'yyyy-MM-dd')
    const monthEnd = format(endOfMonth(viewingDate), 'yyyy-MM-dd')
    const controller = new AbortController()

    homeApi
      .fetchParentMaterialView(
        { studentId, startDate: monthStart, endDate: monthEnd, materialViewType: 'ALL' },
        controller.signal,
      )
      .then((items) => {
        if (controller.signal.aborted) return
        const dates = new Set<CalenderDateStr>()
        items.forEach((item) => {
          dates.add(format(new Date(item.datetime), 'yyyy-MM-dd') as CalenderDateStr)
        })
        setSpecialDates(dates)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        errorHandlerService.handle(err)
      })

    return () => controller.abort()
  }, [viewingDate])

  const isApplicable = tempRange.start !== null && tempRange.end !== null

  const handleApply = () => {
    const { start, end } = tempRange
    if (!start || !end) return

    // 30일 초과 검증 — 시작일 포함이라 +1
    const diffDays = Math.floor((end.dateObj.getTime() - start.dateObj.getTime()) / ONE_DAY) + 1
    if (diffDays > MAX_RANGE_DAYS) {
      modalService.openModal(
        <Modal.Alert confirm={{ onClick: () => modalService.closeModal() }}>
          최대 {MAX_RANGE_DAYS}일까지만 선택할 수 있어요.
          <br />
          기간을 다시 선택해 주세요.
        </Modal.Alert>,
        { modalName: '학부모 기간 선택 30일 초과 알럿' },
      )
      return
    }

    onApply({
      startDate: format(start.dateObj, 'yyyy-MM-dd'),
      endDate: format(end.dateObj, 'yyyy-MM-dd'),
    })
  }

  return (
    <div css={_ModalStyle}>
      <Calendar
        className="picker-calendar"
        type="range"
        selectedRange={tempRange}
        onRangeSelect={setTempRange}
        specialDates={specialDates}
        onPreviousClick={(d) => setViewingDate(d.dateObj)}
        onNextClick={(d) => setViewingDate(d.dateObj)}
      />
      <button type="button" className="apply-btn" onClick={handleApply} disabled={!isApplicable}>
        선택하기
      </button>
    </div>
  )
}

const _ModalStyle = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 360px;
  padding: 0 20px 20px;

  ${mediaQuery.underTablet} {
    width: 328px;
  }

  .picker-calendar {
    max-width: none;
    padding: 0;
  }

  .ms__Calendar_date {
    width: 32px;
    height: 32px;
    padding: 0;
    place-self: center;
  }

  .apply-btn {
    height: 36px;
    border-radius: var(--Radius-200);
    background: var(--Colors-Blue-500);
    color: var(--White);
    ${typo.caption01};
    font-weight: 700;
    cursor: pointer;

    &:disabled {
      background: var(--Colors-Gray-300);
      color: var(--Colors-Gray-500);
      cursor: not-allowed;
    }
  }
`
